import jwtDecode from 'jwt-decode';
import { AppDispatch } from '../store';
import {
  loginSuccessReducer,
  setMeReducer,
  setInitializedReducer,
  logoutReducer,
  setAuthLoading,
  setAuthError,
} from '../reducers/authReducer';
import { loginApi, verifyLoginApi, logoutApi, getMeApi } from '../../api/authApi';
import { setSession } from '../../utils/jwt';
import { disablePush } from '../../utils/pushNotifications';

// Stores the token and the owner's profile once the login (and the code, with two-factor) is done
const finishLogin = (dispatch: AppDispatch, response: any, email: string): boolean => {
  const token =
    response?.token ||
    response?.data?.token ||
    response?.access_token ||
    response?.data?.access_token;
  if (!token) return false;

  setSession(token);

  let subRole = 'owner';
  try {
    const decoded: any = jwtDecode(token);
    if (decoded?.sub) {
      subRole = decoded.sub;
    }
  } catch (e) {
    // Token decode fallback
  }

  const user = response.user || {
    id: subRole,
    name: subRole.toUpperCase() === 'OWNER' ? 'ElectionSetu Owner' : 'Admin User',
    displayName: subRole.toUpperCase() === 'OWNER' ? 'ElectionSetu Owner' : 'Admin User',
    email,
    role: subRole.toUpperCase(),
    status: 'active',
  };

  dispatch(
    loginSuccessReducer({
      user,
      accessToken: token,
      refreshToken: response.refreshToken || response.refresh_token,
    })
  );
  dispatch(setInitializedReducer());
  return true;
};

const failLogin = (dispatch: AppDispatch, error: any, fallback: string) => {
  console.error('Login Error:', error);
  const errorMsg =
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    fallback;
  dispatch(setAuthError(typeof errorMsg === 'string' ? errorMsg : fallback));
  dispatch(setAuthLoading(false));
};

export const loginThunk = (payload: {
  data: { email: string; password: string };
  onSuccess?: (res: any) => void;
  // Two-factor is on: show the code field and send the code with this otp_token (valid 5 minutes)
  onOtpRequired?: (otpToken: string) => void;
  onFail?: (err: any) => void;
}) => async (dispatch: AppDispatch): Promise<void> => {
  dispatch(setAuthLoading(true));
  dispatch(setAuthError(null));

  try {
    const response: any = await loginApi(payload.data);

    if (response?.otp_required && response?.otp_token) {
      dispatch(setAuthLoading(false));
      if (payload.onOtpRequired) payload.onOtpRequired(response.otp_token);
      return;
    }

    if (finishLogin(dispatch, response, payload.data.email)) {
      if (payload.onSuccess) payload.onSuccess(response);
    } else {
      throw new Error(response?.message || 'Login failed: Token not received');
    }
  } catch (error: any) {
    failLogin(dispatch, error, 'Invalid email or password');
    if (payload.onFail) payload.onFail(error);
  }
};

// Second login step when two-factor is on. code: the app's 6 digits or a backup code.
export const verifyLoginThunk = (payload: {
  data: { otp_token: string; code: string };
  email: string;
  onSuccess?: (res: any) => void;
  onFail?: (err: any) => void;
}) => async (dispatch: AppDispatch): Promise<void> => {
  dispatch(setAuthLoading(true));
  dispatch(setAuthError(null));

  try {
    const response: any = await verifyLoginApi({
      otp_token: payload.data.otp_token,
      code: payload.data.code.trim(),
    });
    if (finishLogin(dispatch, response, payload.email)) {
      if (payload.onSuccess) payload.onSuccess(response);
    } else {
      throw new Error(response?.message || 'Login failed: Token not received');
    }
  } catch (error: any) {
    failLogin(dispatch, error, 'Wrong or already used code');
    if (payload.onFail) payload.onFail(error);
  }
};

export const getMeThunk = (payload?: {
  onSuccess?: (data: any) => void;
  onFail?: (err: any) => void;
}) => async (dispatch: AppDispatch): Promise<void> => {
  try {
    const res: any = await getMeApi();
    const me = res?.data || res;
    if (me?.email) {
      dispatch(setMeReducer(me));
    }
    dispatch(setInitializedReducer());
    if (payload?.onSuccess) payload.onSuccess(res);
  } catch (err) {
    dispatch(setInitializedReducer());
    if (payload?.onFail) payload.onFail(err);
  }
};

// POST /owner/logout ends every owner token on every device. The token is
// forgotten here even when that call fails (e.g. it had already expired).
// local: only forget the token on this device (the session ran out by itself).
export const logoutThunk = (options?: { local?: boolean }) => async (dispatch: AppDispatch): Promise<void> => {
  if (!options?.local) {
    // This browser stops getting this login's notification pushes (needs the token, so first)
    await disablePush().catch(() => undefined);
    try {
      await logoutApi();
    } catch (e) {
      // The token is dropped below anyway
    }
  }
  setSession(null);
  dispatch(logoutReducer());
};
